import { useCallback, useEffect, useRef, useState } from 'react'

const MIN_WIDTH = 280
const MAX_WIDTH = 600
const DEFAULT_WIDTH = 320
const KEYBOARD_STEP = 16
const STORAGE_KEY = 'keynews-sidebar-width'

const clamp = (value: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value))

/**
 * Drag-to-resize for the desktop sidebar, persisted to localStorage.
 *
 * Uses Pointer Events rather than mouse events so mouse, touch and pen share one
 * code path — the previous mouse-only implementation left the handle inoperable on
 * touch devices — and exposes a keyboard handler so it is reachable without a
 * pointer at all.
 */
export function useSidebarResize() {
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    const parsed = saved ? parseInt(saved, 10) : NaN
    return Number.isFinite(parsed) ? clamp(parsed) : DEFAULT_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)

  // Kept in a ref so the pointerup listener persists the latest width without
  // re-subscribing on every pixel of movement.
  const widthRef = useRef(sidebarWidth)
  widthRef.current = sidebarWidth

  const persist = useCallback((width: number) => {
    localStorage.setItem(STORAGE_KEY, String(width))
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    // Capture keeps events coming to the handle even when the pointer outruns it.
    e.currentTarget.setPointerCapture(e.pointerId)
    e.preventDefault()
    setIsResizing(true)
  }, [])

  const handleResizeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const delta =
        e.key === 'ArrowLeft' ? -KEYBOARD_STEP : e.key === 'ArrowRight' ? KEYBOARD_STEP : 0
      if (delta === 0) return

      e.preventDefault()
      const next = clamp(widthRef.current + delta)
      setSidebarWidth(next)
      persist(next)
    },
    [persist]
  )

  useEffect(() => {
    if (!isResizing) return

    const handlePointerMove = (e: PointerEvent) => setSidebarWidth(clamp(e.clientX))
    const stop = () => {
      setIsResizing(false)
      persist(widthRef.current)
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', stop)
    document.addEventListener('pointercancel', stop)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', stop)
      document.removeEventListener('pointercancel', stop)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, persist])

  return {
    sidebarWidth,
    isResizing,
    handlePointerDown,
    handleResizeKeyDown,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
  }
}
